import { Alert, Button, Card, Col, Divider, Flex, Popconfirm, Row, Space, Spin, Switch, Tag, Typography, theme } from 'antd';
import { FC } from 'react';
import { BoxArrowUpRight, ExclamationCircle, ExclamationTriangleFill } from 'react-bootstrap-icons';

import { CopyableText, DescriptionBlock, DomainPunycode, EmptyText, OpenInNewTabIcon } from '@/common/components';
import { AppRoutes, BaseQueryParams } from '@/common/interfaces';
import { CommonDrawerTitle } from '@/common/layouts';

import {
  ChangeMainDomainProcessStatus,
  DomainDeployStatus,
  DomainDeployStatusTag,
  DomainGoogleVerificationTokenLabel,
  DomainRegluingsOverview,
  DomainSchemeTag,
  DomainWWWRedirect,
  SiteDomainCachePurging,
  useGetByIdSiteDomainsQuery,
} from '@/modules/Domains';
import { DomainsApiProviders } from '@/modules/DomainsApi';

interface Props {
  siteId: string;
  domainId: string;
  siteDomain: BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data'];
  isChangeSitesMainDisabled: boolean;
  isVerifyDomainLoading: boolean;
  isAddDomainLoading: boolean;
  isGenerateTokenLoading: boolean;
  isDenyDomainAccessLoading: boolean;
  isPurgeDomainCacheLoading: boolean;
  changeMainDomainProcessStatus: ChangeMainDomainProcessStatus | undefined;
  handleChangeSitesMain: () => void;
  handleCheckGoogleVerification: () => void;
  handleAddToSearchConsole: () => void;
  handleGenerateGoogleToken: () => void;
  handleDenyDomainAccess: () => void;
  handlePurgeDomainCache: () => void;
}

export const SiteDomainsDossierView: FC<Props> = ({
  domainId,
  siteId,
  siteDomain: {
    id,
    dnsRecord,
    googleVerificationToken,
    isDeployed,
    isGoogleVerificated,
    isHttps,
    deployStatus,
    isSitesMain,
    name,
    site,
    staticPrefix,
    wwwRedirect,
    googleVerificationErrorMessage,
    isIncludedInSearchConsole,
    isAccessDenied,
    lastCachePurgedAt,
  },
  isVerifyDomainLoading,
  changeMainDomainProcessStatus,
  isChangeSitesMainDisabled,
  isAddDomainLoading,
  isGenerateTokenLoading,
  isDenyDomainAccessLoading,
  isPurgeDomainCacheLoading,
  handleAddToSearchConsole,
  handleCheckGoogleVerification,
  handleChangeSitesMain,
  handleGenerateGoogleToken,
  handleDenyDomainAccess,
  handlePurgeDomainCache,
}) => {
  const { token } = theme.useToken();

  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Space>
            {isSitesMain ? (
              <Spin size="small" spinning={changeMainDomainProcessStatus === ChangeMainDomainProcessStatus.InProgress}>
                <Tag key="isSitesMain" color="blue">
                  Основний
                </Tag>
              </Spin>
            ) : (
              <Popconfirm
                disabled={isChangeSitesMainDisabled || changeMainDomainProcessStatus === ChangeMainDomainProcessStatus.InProgress}
                title="Зробити цей домен основним?"
                onConfirm={handleChangeSitesMain}
                okType="danger"
                okText="Так"
                icon={<ExclamationCircle color={token.colorError} />}
                placement="bottom"
              >
                <Button
                  size="small"
                  type="primary"
                  loading={
                    isChangeSitesMainDisabled || changeMainDomainProcessStatus === ChangeMainDomainProcessStatus.InProgress
                  }
                  disabled={
                    isChangeSitesMainDisabled || changeMainDomainProcessStatus === ChangeMainDomainProcessStatus.InProgress
                  }
                >
                  Зробити основним
                </Button>
              </Popconfirm>
            )}
            <DomainSchemeTag simple={!isSitesMain} isDeployed={isDeployed} isHttps={isHttps} />
          </Space>
        }
      />

      <Row gutter={[0, 16]}>
        <Col span={24}>
          {isDeployed && (
            <>
              <Button
                type="dashed"
                block
                icon={<OpenInNewTabIcon size={18} />}
                title="Відкрити в новій вкладці"
                target="_blank"
                href={`${isHttps ? 'https' : 'http'}://${wwwRedirect === DomainWWWRedirect.WithoutWWWToWWW ? 'www.' : ''}${name}`}
              >
                Відкрити в новій вкладці
              </Button>
              <br />
              <br />
            </>
          )}

          <DescriptionBlock
            items={[
              [
                'Назва',
                <a key="siteDomain.name" href={AppRoutes.DomainsById.replace(':domainId', id)} target="_blank" rel="noreferrer">
                  <CopyableText textToCopy={DomainPunycode.stringFormat(name)}>
                    <DomainPunycode domainName={name} />
                  </CopyableText>
                  &nbsp;
                  <BoxArrowUpRight />
                </a>,
              ],
            ]}
          />

          <DescriptionBlock items={[['Стан', <DomainDeployStatusTag key="siteDomain.deployStatus" status={deployStatus} />]]} />

          <DescriptionBlock items={[['Префікс статики', staticPrefix || <EmptyText key="siteDomain.staticPrefix" />]]} />

          <DescriptionBlock
            layout="horizontal"
            items={[
              [
                'Заборонити доступ (для користувачів)',
                <Switch
                  key="siteDomain.isAccessDenied"
                  checked={isAccessDenied}
                  loading={isDenyDomainAccessLoading}
                  onClick={handleDenyDomainAccess}
                />,
              ],
            ]}
          />

          <Divider />

          {site?.searchConsoleAccount ? (
            <DescriptionBlock
              items={[
                [
                  'Додано в Search Console',
                  <Flex key="handleCheckGoogleVerification" justify="space-between" align="center" style={{ width: '100%' }}>
                    {isIncludedInSearchConsole ? 'Так' : 'Ні'}
                    <Button
                      size="small"
                      type="primary"
                      loading={isAddDomainLoading}
                      disabled={isAddDomainLoading}
                      onClick={handleAddToSearchConsole}
                    >
                      Додати
                    </Button>
                  </Flex>,
                ],
              ]}
            />
          ) : (
            <Alert
              type="warning"
              icon={<ExclamationTriangleFill color={token.colorWarning} />}
              message="Для отримання токену верифікації потрібно додати акакунт Search Console до цього сайту."
              showIcon
            />
          )}

          {site?.searchConsoleAccount && (
            <DescriptionBlock
              items={[
                [
                  'Токен верифікації Google',
                  <Flex key="siteDomain.googleVerificationToken" vertical gap={8} style={{ width: '100%' }}>
                    {googleVerificationToken ? (
                      <CopyableText textToCopy={googleVerificationToken}>{googleVerificationToken}</CopyableText>
                    ) : (
                      <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                        <EmptyText key="siteDomain.googleVerificationToken" />

                        <Button
                          size="small"
                          type="primary"
                          loading={isGenerateTokenLoading}
                          disabled={isGenerateTokenLoading}
                          onClick={handleGenerateGoogleToken}
                        >
                          Згенерувати
                        </Button>
                      </Flex>
                    )}
                    {googleVerificationToken && !dnsRecord?.apiProvider && (
                      <Alert
                        type="warning"
                        message="Ви повинні додати цей токен в TXT запис вашого домену на провайдері вручну."
                        description={
                          <>
                            Формат: google-site-verification=<b>{'{token}'}</b>; ttl=1h
                          </>
                        }
                        showIcon
                      />
                    )}
                  </Flex>,
                ],
              ]}
            />
          )}

          {site?.searchConsoleAccount && (
            <DescriptionBlock
              items={[
                [
                  'Верифікація Google',
                  <Flex key="handleCheckGoogleVerification" justify="space-between" align="center" style={{ width: '100%' }}>
                    <DomainGoogleVerificationTokenLabel isGoogleVerificated={isGoogleVerificated} />
                    <Button
                      size="small"
                      type="primary"
                      loading={isVerifyDomainLoading}
                      disabled={isVerifyDomainLoading}
                      onClick={handleCheckGoogleVerification}
                    >
                      Перевірити
                    </Button>
                  </Flex>,
                ],
              ]}
            />
          )}

          {!isVerifyDomainLoading && googleVerificationErrorMessage && (
            <Alert type="error" message={googleVerificationErrorMessage} />
          )}
        </Col>

        <Col span={24}>
          <Divider style={{ marginBlock: 12 }} />
        </Col>

        <Col span={24}>
          <SiteDomainCachePurging
            loading={isPurgeDomainCacheLoading}
            deployStatus={deployStatus}
            lastCachePurgedAt={lastCachePurgedAt}
            apiProvider={dnsRecord.apiProvider}
            handlePurgeDomainCache={handlePurgeDomainCache}
          />
        </Col>

        <Col span={24}>
          <Divider style={{ marginBlock: 12 }} />
        </Col>

        <Col span={24}>
          <DomainRegluingsOverview siteId={siteId} domainId={domainId} />
        </Col>
      </Row>
    </>
  );
};
